import { Button } from 'reactstrap';

import type { DeliveryRuleDefinition } from 'domain-model/types/oms';
import domainModel from 'domain-model';
import _ from 'lodash';
import React from 'react';

import withFormsyValidations from '../../withFormsyValidations';
import SimplifiedRulesViewer from './SimplifiedRulesViewer';
import SimplifiedRulesInput from './SimplifiedRulesInput';
import ExtendedRulesViewer from './ExtendedRulesViewer';
import ExtendedRulesInput from './ExtendedRulesInput';

type DeliveryRulesEditorType =
  typeof domainModel.deliveryRules.DeliveryRulesEditor;

const { DeliveryRule, DeliveryRulesEditor, DeliveryRulesGeneralRegion } =
  domainModel.deliveryRules;

const DeliveryRulesEntityType = {
  orderFullfillmentCenter: 'orderFullfillmentCenter',
  customerSubUnit: 'customerSubUnit',
  supplierCustomer: 'supplierCustomer',
  supplierConnection: 'supplierConnection',
};

interface DeliveryRulesInputProps {
  name: string;
  setValue: (value: DeliveryRuleDefinition[], something: boolean) => void;
  region?: string;
  disabled?: boolean;
  onChange: (name: string, value: DeliveryRuleDefinition[]) => void;
  errorMessage?: string;
  deliveryRulesEditor?: DeliveryRulesEditorType;
  value: DeliveryRuleDefinition[];
  entityType?: (typeof DeliveryRulesEntityType)[keyof typeof DeliveryRulesEntityType];
  isOverride?: boolean;
}
const DeliveryRulesInput: React.FC<DeliveryRulesInputProps> = props => {
  const { name, setValue, disabled, value, entityType, isOverride } = props;
  const [openRegion, setOpenRegion] = React.useState<string | null>(
    DeliveryRulesGeneralRegion,
  );

  const showSimplifiedComponent =
    entityType === DeliveryRulesEntityType.customerSubUnit ||
    entityType === DeliveryRulesEntityType.supplierConnection;

  const canHaveRegions =
    entityType === DeliveryRulesEntityType.orderFullfillmentCenter &&
    name !== 'deliveryRuleOverrides';

  const deliveryRulesEditor = new DeliveryRulesEditor();
  deliveryRulesEditor.setRules(value);

  const handleChange = () => {
    const finalRules = deliveryRulesEditor.getDeliveryRuleDefinitions();

    props.onChange(name, finalRules);

    setValue(finalRules, false);
  };

  const handleOnRegionChanged = (newRegion: string) => {
    setOpenRegion(newRegion);
  };

  const regions = _.uniq([
    DeliveryRulesGeneralRegion,
    ...deliveryRulesEditor.getUniqueRegions(),
  ]);

  const handleRegionToggle = (region: string) => {
    setOpenRegion(openRegion === region ? null : region);
  };

  if (showSimplifiedComponent) {
    if (disabled) {
      return (
        <div>
          {regions.map((region: string) => (
            <SimplifiedRulesViewer
              key={region}
              region={region}
              deliveryRulesEditor={deliveryRulesEditor}
            />
          ))}
        </div>
      );
    }
    return (
      <div>
        {regions.map((region: string) => (
          <SimplifiedRulesInput
            key={region}
            region={region}
            deliveryRulesEditor={deliveryRulesEditor}
            onChange={handleChange}
          />
        ))}
      </div>
    );
  }

  if (disabled) {
    return (
      <div>
        {regions.map((region: string) => (
          <ExtendedRulesViewer
            key={region}
            region={region}
            deliveryRulesEditor={deliveryRulesEditor}
            onlyExplicitDates={isOverride}
          />
        ))}
      </div>
    );
  }

  return (
    <div>
      {regions.map((region: string) => (
        <ExtendedRulesInput
          key={region}
          region={region}
          deliveryRulesEditor={deliveryRulesEditor}
          onChange={handleChange}
          onRegionChanged={handleOnRegionChanged}
          isOpen={disabled || openRegion === region}
          onToggle={() => handleRegionToggle(region)}
          onlyExplicitDates={isOverride}
        />
      ))}
      {canHaveRegions && (
        <Button
          onClick={() => {
            const newRule = new DeliveryRule();
            newRule.setDeliveryWeekday('mon');
            newRule.setRegion('xxxxx');
            deliveryRulesEditor.addDeliveryRule(newRule);
            handleChange();
          }}
        >
          Skapa ny region
        </Button>
      )}
    </div>
  );
};

DeliveryRulesInput.defaultProps = {
  disabled: false,
  setValue: () => {},
  value: [],
};

export { DeliveryRulesInput };
export default withFormsyValidations(DeliveryRulesInput, () => ({}));
